import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import bannerPurpleMetricsWeb from 'assets/images/banner-purple-metrics-web.png';
import bannerPurpleMetricsMobile from 'assets/images/banner-purple-metrics-mobile.png';
import bannerWhatsApp from 'assets/images/banner-aviso-numero-whats.png';

import { buildWhatsAppHref } from 'utils/buildContactLinks';
import {
  BRAND_SURVEY_URL,
  WHATSAPP_OFFICIAL_PHONE,
} from '../homeBannerConfig';
import { ImageBannerPurpleMetrics, LinkPurpleMetrics } from '../styles';
import {
  CarouselContainer,
  CarouselDot,
  CarouselDots,
  CarouselSlide,
  CarouselTrack,
  CarouselViewport,
} from './styles';

const AUTO_PLAY_INTERVAL_MS = 6000;

type BannerSlide = {
  id: string;
  webSrc: string;
  mobileSrc: string;
  href: string;
  alt: string;
  ariaLabel: string;
};

const HomeBannerCarousel = (): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: 620 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const slides = useMemo<BannerSlide[]>(
    () => [
      {
        id: 'brand-survey',
        webSrc: bannerPurpleMetricsWeb,
        mobileSrc: bannerPurpleMetricsMobile,
        href: BRAND_SURVEY_URL,
        alt: 'Pesquisa de marca — abre em nova aba',
        ariaLabel: 'Pesquisa de marca — abre em nova aba',
      },
      {
        id: 'whatsapp',
        webSrc: bannerWhatsApp,
        mobileSrc: bannerWhatsApp,
        href: buildWhatsAppHref(WHATSAPP_OFFICIAL_PHONE),
        alt: 'WhatsApp Oficial Ultragaz — Fale com a gente',
        ariaLabel: 'WhatsApp Oficial Ultragaz — abre em nova aba',
      },
    ],
    []
  );

  const goToSlide = useCallback(
    (index: number) => {
      setActiveIndex((index + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    if (isPaused || slides.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, AUTO_PLAY_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isPaused, slides.length]);

  return (
    <CarouselContainer
      role="region"
      aria-label="Banners do portal"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <CarouselViewport>
        <CarouselTrack $activeIndex={activeIndex}>
          {slides.map((slide) => (
            <CarouselSlide key={slide.id}>
              <LinkPurpleMetrics
                href={slide.href}
                rel="noreferrer"
                target="_blank"
                aria-label={slide.ariaLabel}
              >
                <ImageBannerPurpleMetrics
                  src={isMobile ? slide.mobileSrc : slide.webSrc}
                  alt={slide.alt}
                />
              </LinkPurpleMetrics>
            </CarouselSlide>
          ))}
        </CarouselTrack>
      </CarouselViewport>

      {slides.length > 1 && (
        <CarouselDots role="tablist" aria-label="Navegação dos banners">
          {slides.map((slide, index) => (
            <CarouselDot
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={activeIndex === index}
              aria-label={`Banner ${index + 1} de ${slides.length}`}
              $active={activeIndex === index}
              onClick={() => goToSlide(index)}
            />
          ))}
        </CarouselDots>
      )}
    </CarouselContainer>
  );
};

export default HomeBannerCarousel;
